import PageWrapper from 'common/components/PageWrapper'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useMutateUpdateInventorySettings } from 'modules/inventory/api/mutation'
import { useInventorySettings } from 'modules/inventory/api/queries'
import { InventorySettingCode, InventorySettingFeatureCode } from 'modules/inventory/api/type'
import { GreenSwitch } from 'modules/inventory/components/IsActiveSwitch'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { PATH_DASHBOARD } from 'routes/paths'

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Box, CircularProgress, IconButton, Stack, Typography, alpha } from '@mui/material'

const PAGE_TITLE_KEY: Record<string, string> = {
  food: 'settings.display.title_food',
  medicine: 'settings.display.title_medicine',
}

const CODE_LABEL_KEY: Record<string, string> = {
  show_lot_number: 'settings.display.show_lot_number',
  show_remaining_stock: 'settings.display.show_remaining_stock',
}

const DESCRIPTION_KEY: Record<string, string> = {
  food_show_lot_number: 'settings.display.description_food_lot',
  food_show_remaining_stock: 'settings.display.description_food_remaining',
  medicine_show_lot_number: 'settings.display.description_medicine_lot',
  medicine_show_remaining_stock: 'settings.display.description_medicine_remaining',
}

interface MockItem {
  name: string
  unit: string
  lot: string | null
  remaining: string
  selected?: boolean
}

const MOCK_PREVIEW_ITEMS: Record<string, MockItem[]> = {
  food: [
    { name: 'TSA40%3P', unit: 'กก.', lot: 'AB1106', remaining: '2,000' },
    { name: 'กรุงไทย ปกติ 2', unit: 'กก.', lot: null, remaining: '850' },
    { name: 'โกรเบสท์ 40%เร่งสี 2', unit: 'กก.', lot: null, remaining: '1,240' },
    { name: 'โกรเบสท์ 40%เร่งสี 2S', unit: 'กก.', lot: null, remaining: '320', selected: true },
  ],
  medicine: [
    { name: 'คลอรีนน้ำ', unit: 'ตัน', lot: null, remaining: '1.20' },
    { name: 'Blue Origin', unit: 'กก.', lot: null, remaining: '48', selected: true },
    { name: 'Red Booster', unit: 'ซอง', lot: null, remaining: '24' },
    { name: 'Sodium', unit: 'ลิตร', lot: '01', remaining: '180' },
  ],
}

const isValidFeatureCode = (value: string): value is InventorySettingFeatureCode =>
  value === 'food' || value === 'medicine'

const isValidSettingCode = (value: string): value is InventorySettingCode =>
  value === 'show_lot_number' || value === 'show_remaining_stock'

// enforce_stock มีหน้าตั้งค่าของตัวเองแล้ว (การตัดสต๊อก) ไม่ต้องแสดงซ้ำในหน้านี้
// แม้ backend จะส่งมันมาในกลุ่ม settings เดียวกันกับ show_lot_number/show_remaining_stock ก็ตาม
const DISPLAY_SETTINGS_PAGE_CODES = new Set(['show_lot_number', 'show_remaining_stock'])

const InventoryDisplaySettingsPage = () => {
  const { t } = useTranslation('warehouse')
  const navigate = useNavigate()
  const { featureCode } = useParams<{ featureCode: string }>()
  const canManage = usePermitToActionFeature([CRUDAction.Update], FeatureKey.Inventory)

  const { data, isLoading } = useInventorySettings()
  const { mutate } = useMutateUpdateInventorySettings()
  const [savingCodes, setSavingCodes] = useState<Set<string>>(new Set())

  const group = data?.find((g) => g.feature_code === featureCode)
  const mockItems = featureCode ? MOCK_PREVIEW_ITEMS[featureCode] : undefined
  const showLotNumber =
    group?.settings.find((s) => s.code === 'show_lot_number')?.is_enabled ?? true
  const showRemainingStock =
    group?.settings.find((s) => s.code === 'show_remaining_stock')?.is_enabled ?? true

  const LINKS = [
    { name: t('inventory.title'), href: PATH_DASHBOARD.inventory.product },
    { name: t('inventory.settings.title'), href: PATH_DASHBOARD.inventory.settings },
    { name: t(PAGE_TITLE_KEY[featureCode ?? ''] ?? '', { defaultValue: featureCode }) },
  ]

  const handleToggle = (code: string, isEnabled: boolean) => {
    if (!featureCode || !isValidFeatureCode(featureCode) || !isValidSettingCode(code)) return
    if (savingCodes.has(code)) return
    setSavingCodes((prev) => new Set(prev).add(code))
    mutate(
      { settings: [{ feature_code: featureCode, code, is_enabled: isEnabled }] },
      {
        onSettled: () => {
          setSavingCodes((prev) => {
            const next = new Set(prev)
            next.delete(code)
            return next
          })
        },
      }
    )
  }

  return (
    <PageWrapper
      title={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={() => navigate(PATH_DASHBOARD.inventory.settings)}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="h4">
            {t(PAGE_TITLE_KEY[featureCode ?? ''] ?? '', { defaultValue: featureCode })}
          </Typography>
        </Stack>
      }
      titleHead={t(PAGE_TITLE_KEY[featureCode ?? ''] ?? '', { defaultValue: featureCode })}
      links={LINKS}
    >
      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <Box sx={{ maxWidth: 500 }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: '0px 2px 8px 0px #00000014',
              overflow: 'hidden',
            }}
          >
            {(() => {
              const visibleSettings =
                group?.settings.filter((item) => DISPLAY_SETTINGS_PAGE_CODES.has(item.code)) ?? []
              return visibleSettings.map((item, index) => (
                <Box
                  key={item.code}
                  sx={{
                    p: 2.5,
                    borderBottom: index < visibleSettings.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t(CODE_LABEL_KEY[item.code] ?? item.code, { defaultValue: item.code })}
                    </Typography>
                    <GreenSwitch
                      checked={item.is_enabled}
                      disabled={!canManage || savingCodes.has(item.code)}
                      onChange={(_, checked) => handleToggle(item.code, checked)}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t(DESCRIPTION_KEY[`${featureCode}_${item.code}`] ?? '', { defaultValue: '' })}
                  </Typography>
                </Box>
              ))
            })()}
          </Box>

          {mockItems && (
            <PreviewSection
              items={mockItems}
              showLotNumber={showLotNumber}
              showRemainingStock={showRemainingStock}
            />
          )}
        </Box>
      )}
    </PageWrapper>
  )
}

interface PreviewSectionProps {
  items: MockItem[]
  showLotNumber: boolean
  showRemainingStock: boolean
}

const PreviewSection = ({ items, showLotNumber, showRemainingStock }: PreviewSectionProps) => {
  const { t } = useTranslation('warehouse')
  const selected = items.find((i) => i.selected) ?? items[0]

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('settings.display.preview_title', { defaultValue: 'ตัวอย่างผลลัพธ์ในฟอร์มจดบันทึก' })}
      </Typography>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0px 2px 8px 0px #00000014',
          p: 1.5,
        }}
      >
        {/* mimics the closed/open Autocomplete input row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid',
            borderColor: 'success.main',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mb: 1,
          }}
        >
          <Typography variant="body2">{selected.name}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CloseIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <KeyboardArrowUpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </Stack>
        </Box>

        {/* mimics the open option list */}
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          {items.map((item) => (
            <Box
              key={item.name}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: 1,
                px: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: item.selected ? alpha('#4CAF50', 0.08) : 'transparent',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Box>
                <Typography fontWeight={700} color="text.primary" fontSize={14}>
                  {item.name}
                </Typography>
                {showLotNumber && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        mr: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography fontSize={11} color="text.secondary">
                        {t('settings.display.lot', { defaultValue: 'ล็อต' })}
                      </Typography>
                    </Box>
                    <Typography fontSize={13} color="text.secondary">
                      {item.lot ?? '-'}
                    </Typography>
                  </Box>
                )}
              </Box>
              {showRemainingStock && (
                <Typography fontSize={13} color="text.secondary">
                  {t('settings.display.remaining', { defaultValue: 'เหลือ' })} {item.remaining}{' '}
                  {item.unit}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default InventoryDisplaySettingsPage
